/**
 * 3 Nov 2026 (D-1103-PAIR): "Usually with ..." — on Book, the tenant's regular volunteer and when that volunteer said
 * they are free. Pure functions only (the server and the screen share them); no list, no column.
 *
 *   VOL_USUAL_PARTNER  Yes (default)  the card on the volunteer step, and "Marked this time" in the list.
 *   VOL_USUAL_PARTNER  No             exactly the volunteer step as it was.
 *
 * The regular volunteer: the most completed or confirmed visits with this tenant in this program in the last 6 months,
 * at least 2, up to 2 names. Ties: the more recent visit, then the name. A visit that did not happen (the volunteer
 * could not make it, it was cancelled before it took place, "didn't happen") does not count.
 *
 * The next free times, over the next 14 days (tomorrow through day 14): the single dates the volunteer marked come
 * first, then the weekly days. A "can't" date is never offered; a weekly day with a "can't" date in the window is
 * listed by its dates instead of "every ...". Nothing here assigns anyone: a button fills the day and the part and
 * picks the volunteer to ASK; the confirm-by-volunteer flow is unchanged.
 */
import { addDaysToKey, keyToIsoDay } from './dates';
import { mondayIndex, type Availability, type Part } from './volunteers';

export const USUAL_PARTNER_KEY = 'VOL_USUAL_PARTNER';
export const USUAL_MIN_VISITS = 2;
export const USUAL_MAX_NAMES = 2;
export const USUAL_MONTHS = 6;
export const FREE_WINDOW_DAYS = 14;
/** At most this many "Book ..." buttons on the card. */
export const FREE_MAX_BUTTONS = 6;

type Settings = Record<string, string | undefined> | undefined | null;

/** On unless the row says No (a site with no row is on). */
export function usualPartnerOn(settings: Settings): boolean {
  return !/^(no|false|off|0)$/i.test(String(settings?.[USUAL_PARTNER_KEY] ?? '').trim());
}

/** The day six months before `todayKey` (calendar months, clamped to the month's end). */
export function sixMonthsBefore(todayKey: string, months = USUAL_MONTHS): string {
  let y = Number(todayKey.slice(0, 4));
  let m = Number(todayKey.slice(4, 6)) - months;
  const d = Number(todayKey.slice(6, 8));
  while (m < 1) {
    m += 12;
    y -= 1;
  }
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${y}${String(m).padStart(2, '0')}${String(Math.min(d, last)).padStart(2, '0')}`;
}

export interface CountedVisit {
  volunteerId: string;
  dayKey: string;
}

export interface RegularPick {
  volunteerId: string;
  visits: number;
  lastDayKey: string;
}

/**
 * Who the tenant usually sees. `visits` are the visits that count already (this tenant, this program, not a visit that
 * did not happen); the window and the minimum are applied here. `names` breaks a last tie.
 */
export function regularVolunteers(visits: readonly CountedVisit[], todayKey: string, names: (id: string) => string = (id) => id): RegularPick[] {
  const from = sixMonthsBefore(todayKey);
  const by = new Map<string, RegularPick>();
  for (const v of visits) {
    if (v.dayKey < from) continue;
    const cur = by.get(v.volunteerId);
    if (cur) {
      cur.visits += 1;
      if (v.dayKey > cur.lastDayKey) cur.lastDayKey = v.dayKey;
    } else by.set(v.volunteerId, { volunteerId: v.volunteerId, visits: 1, lastDayKey: v.dayKey });
  }
  return [...by.values()]
    .filter((p) => p.visits >= USUAL_MIN_VISITS)
    .sort((a, b) => b.visits - a.visits || (a.lastDayKey < b.lastDayKey ? 1 : a.lastDayKey > b.lastDayKey ? -1 : 0) || names(a.volunteerId).localeCompare(names(b.volunteerId)))
    .slice(0, USUAL_MAX_NAMES);
}

/* ------------------------------------------------------------------------------------------------
 * The next free times
 * ---------------------------------------------------------------------------------------------- */

export interface FreeTime {
  kind: 'date' | 'weekly';
  /** The day the button fills: the date itself, or the next time this weekday comes (yyyyMMdd). */
  dayKey: string;
  /** Monday = 0 … Sunday = 6. */
  weekday: number;
  /** The parts of the day they marked (a single date they marked with no part on that weekday has none: any time). */
  parts: Part[];
  /** Review (D-1103-PAIR-9): the exact window they gave for this date or weekday (minutes from midnight), when they gave one. */
  win?: { from: number; to?: number };
}

const winOf = (a: Availability, k: string, wd: number): { from: number; to?: number } | undefined => {
  const w = a.onWin?.[keyToIsoDay(k)] ?? a.weeklyWin?.[String(wd)];
  return w ? { from: w.from, ...(w.to !== undefined ? { to: w.to } : {}) } : undefined;
};
const withWin = (f: FreeTime, w: { from: number; to?: number } | undefined): FreeTime => (w ? { ...f, win: w } : f);

const PART_ORDER: readonly Part[] = ['am', 'pm', 'eve'];
const inOrder = (ps: readonly Part[]) => PART_ORDER.filter((p) => ps.includes(p));

/**
 * The volunteer's free times in the 14 days after `todayKey`. Dates they marked first (in date order), then each weekly
 * day that has no "can't" date in the window ("every Wednesday evening"). A weekday with a "can't" date in the window
 * is listed by its other dates instead.
 */
export function nextFreeTimes(a: Availability, todayKey: string): FreeTime[] {
  const iso = (k: string) => keyToIsoDay(k);
  const days = Array.from({ length: FREE_WINDOW_DAYS }, (_, i) => addDaysToKey(todayKey, i + 1));
  const dates: FreeTime[] = [];
  const weekly: FreeTime[] = [];
  const brokenWeekdays = new Set<number>();
  for (const k of days) {
    const wd = mondayIndex(k);
    const parts = inOrder(a.weekly[wd] ?? []);
    if (a.off.includes(iso(k))) {
      if (parts.length) brokenWeekdays.add(wd);
      continue;
    }
    if (a.on.includes(iso(k))) dates.push(withWin({ kind: 'date', dayKey: k, weekday: wd, parts }, a.onWin?.[iso(k)]));
  }
  for (let wd = 0; wd < 7; wd++) {
    const parts = inOrder(a.weekly[wd] ?? []);
    if (!parts.length || brokenWeekdays.has(wd)) continue;
    const first = days.find((k) => mondayIndex(k) === wd && !a.on.includes(iso(k)));
    // a weekday whose every date in the window is already listed as a marked date needs no "every ..." line
    if (first) weekly.push(withWin({ kind: 'weekly', dayKey: first, weekday: wd, parts }, a.weeklyWin?.[String(wd)]));
  }
  // a broken weekday still offers its other (not "can't") dates as dates
  for (const wd of brokenWeekdays) {
    const parts = inOrder(a.weekly[wd] ?? []);
    for (const k of days) {
      if (mondayIndex(k) !== wd || a.off.includes(iso(k)) || a.on.includes(iso(k))) continue;
      dates.push(withWin({ kind: 'date', dayKey: k, weekday: wd, parts }, winOf(a, k, wd)));
    }
  }
  dates.sort((x, y) => (x.dayKey < y.dayKey ? -1 : x.dayKey > y.dayKey ? 1 : 0));
  return [...dates, ...weekly];
}

/* ------------------------------------------------------------------------------------------------
 * Words (the same pure functions give the card its sentence and the button its name)
 * ---------------------------------------------------------------------------------------------- */

type Lang = 'en' | 'es';
const DAY_SHORT = { en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], es: ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'] } as const;
const DAY_LONG = { en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], es: ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'] } as const;
const MONTH = { en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'] } as const;
const PART_WORD = { en: { am: 'morning', pm: 'afternoon', eve: 'evening' }, es: { am: 'por la mañana', pm: 'por la tarde', eve: 'por la noche' } } as const;

const partsWords = (ps: readonly Part[], lang: Lang): string => {
  if (!ps.length) return '';
  if (ps.length === 3) return lang === 'es' ? 'todo el día' : 'all day';
  return ps.map((p) => PART_WORD[lang][p]).join(lang === 'es' ? ' y ' : ' and ');
};

/** "Thu 6 Nov" / "jue 6 nov". */
export function freeDateWords(dayKey: string, lang: Lang): string {
  const wd = mondayIndex(dayKey);
  return `${DAY_SHORT[lang][wd]} ${Number(dayKey.slice(6, 8))} ${MONTH[lang][Number(dayKey.slice(4, 6)) - 1]}`;
}

/** "2:00 PM" from minutes after midnight. */
const clock = (m: number): string => {
  const h = Math.floor(m / 60);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
/** "from 2:00 PM" / "2:00 PM to 6:00 PM" (es: "desde las 2:00 PM" / "de 2:00 PM a 6:00 PM"). */
const winWords = (w: { from: number; to?: number }, lang: Lang): string =>
  w.to !== undefined ? (lang === 'es' ? `de ${clock(w.from)} a ${clock(w.to)}` : `${clock(w.from)} to ${clock(w.to)}`) : lang === 'es' ? `desde las ${clock(w.from)}` : `from ${clock(w.from)}`;

/** "Thu 6 Nov afternoon" / "every Wednesday evening" / "Thu 13 Nov from 3:00 PM". Used for the sentence. */
export function freeTimeWords(f: FreeTime, lang: Lang): string {
  const parts = f.win ? winWords(f.win, lang) : partsWords(f.parts, lang);
  if (f.kind === 'date') return parts ? `${freeDateWords(f.dayKey, lang)} ${parts}` : freeDateWords(f.dayKey, lang);
  const day = DAY_LONG[lang][f.weekday]!;
  return lang === 'es' ? `todos los ${day}${f.weekday >= 5 ? 's' : ''}${parts ? ` ${parts}` : ''}` : `every ${day}${parts ? ` ${parts}` : ''}`;
}

/** The part a button fills in: the first part they marked (null = no part, keep the time already chosen). */
export const buttonPart = (f: FreeTime): Part | null => f.parts[0] ?? null;

/** The words on the button: "Thu 6 Nov afternoon" for the day and part it fills ("Book" and the name are the card's). */
export function buttonWhen(f: FreeTime, lang: Lang): string {
  if (f.win) return `${freeDateWords(f.dayKey, lang)} ${lang === 'es' ? 'a las' : 'at'} ${clock(f.win.from)}`;
  const p = buttonPart(f);
  return p ? `${freeDateWords(f.dayKey, lang)} ${PART_WORD[lang][p]}` : freeDateWords(f.dayKey, lang);
}

/** A time of day the button sets for a part (10:00 am, 2:00 pm, 5:30 pm) — the office can change it on the next screen. */
export const PART_MINUTES: Record<Part, number> = { am: 10 * 60, pm: 14 * 60, eve: 17 * 60 + 30 };

/** The time a button sets: the start of their exact window, else the part's usual time, else none (keep what is chosen). */
export function buttonMinutes(f: FreeTime): number | null {
  if (f.win) return f.win.from;
  const p = buttonPart(f);
  return p ? PART_MINUTES[p] : null;
}

/** The sentence: "Maria C.'s next free times: Thu 6 Nov afternoon, Tue 11 Nov morning, every Wednesday evening." */
export function freeSentence(first: string, times: readonly FreeTime[], lang: Lang): string {
  const list = times.map((f) => freeTimeWords(f, lang)).join(', ');
  return lang === 'es' ? `Próximos horarios libres de ${first}: ${list}.` : `${first}'s next free times: ${list}.`;
}

/** "Maria C." from the names on the volunteer's file. */
export function shortName(first: string, last: string): string {
  const f = first.trim();
  const l = last.trim();
  return l ? `${f} ${l.slice(0, 1).toUpperCase()}.` : f;
}

/* ------------------------------------------------------------------------------------------------
 * The card's data (server → screen)
 * ---------------------------------------------------------------------------------------------- */

export interface UsualPartnerEntry {
  volunteerId: string;
  /** "Maria C." */
  name: string;
  firstName: string;
  visits: number;
  /** False: they have not marked any availability (the card offers the availability link). */
  hasAvailability: boolean;
  times: FreeTime[];
}

export interface UsualPartnerView {
  /** false: the switch is off (or the role may not see volunteers) — the screen draws nothing. */
  enabled: boolean;
  partners: UsualPartnerEntry[];
  /** Volunteers (ids) who marked the day and time asked about — "Marked this time". Empty when no day was asked about. */
  marked: string[];
}
